/* nh-cart-guard.js — ONE DOOR FOR EVERY CART WRITE ON THE SITE.

   ⛔ 12 Sep 2026 — WHY THIS EXISTS. A buyer pressed "Add all to cart" with seven
   products. The bulk POST /cart/add.js came back 429, and the page answered by
   firing seven MORE adds, one per product, back to back, with no gap and no check
   of the status code. Every one was refused, and every one pushed the throttle
   window further out. The free-can reconciler was doing the same from the other
   side, and the cart upsell fired one full section fetch per product in the cart,
   so the biggest orders made the most load. The click meant to fill the cart was
   locking the buyer out of it.

   ⛔⛔ AND IT WRAPS window.fetch, NOT JUST OUR OWN CALLS. Owner, when told the
   block was tied to one IP: *"that is fucking bullshit, iy should work on eveyr
   IP"*. He is right. There are 37 cart-adjacent fetches across this theme, in
   vendor files and app files nobody here should be editing one by one, and any of
   them can burst. Patching every caller is a losing game; patching the door is
   not. Anything that POSTs to /cart/{add,change,update,clear} goes through here
   whether it knows it or not, including code installed tomorrow.

   What it does, and nothing more:
     1. ONE AT A TIME, with a small gap. Cart writes queue instead of bursting.
     2. STOP WHEN TOLD TO STOP. A 429/430/503 opens a cool-down; every write
        during it is refused locally, instantly, with no network call at all. Each
        consecutive refusal doubles the hold, to a ceiling of five minutes, so a
        long block is not answered by knocking every sixty seconds. One clean
        answer wipes the record.
     3. NEVER WAIT FOREVER. Twelve seconds and the request is abandoned.
     4. STAY INVISIBLE. Intercepted calls get a real Response back, so every
        existing caller behaves exactly as it did.

   Reads (GET /cart.js, ?sections=) are deliberately NOT touched: they are cheap,
   they are how the page recovers, and serialising them would make the cart feel
   slower for no gain. */
(function () {
  if (window.NHCart) return;

  var MIN_GAP_MS      = 150;    /* between two writes from this tab */
  var COOLDOWN_MS     = 30000;  /* hold after a refusal, when the server names no time */
  var TIMEOUT_MS      = 12000;  /* a healthy write takes ~1.2s; ten times that is dead */
  var TIMEOUT_HOLD_MS = 5000;   /* a slow answer is not a refusal: pause, do not punish */
  var COOL_KEY        = 'nh_cart_cool_until';
  var STRIKE_KEY      = 'nh_cart_strikes';

  /* POSTs to these are writes. Everything else, including every read, is left alone. */
  var WRITE_RE = /\/cart\/(add|change|update|clear)(\.js)?(\?|$)/;

  var rawFetch = window.fetch ? window.fetch.bind(window) : null;
  if (!rawFetch) return;                     /* no fetch: nothing to guard */

  var queue = Promise.resolve();
  var lastAt = 0;

  function now() { return +new Date(); }

  function coolUntil() {
    try { return parseInt(sessionStorage.getItem(COOL_KEY), 10) || 0; } catch (e) { return 0; }
  }
  function setCool(ms) {
    try { sessionStorage.setItem(COOL_KEY, String(now() + ms)); } catch (e) {}
  }
  function clearCool() {
    try { sessionStorage.removeItem(COOL_KEY); sessionStorage.removeItem(STRIKE_KEY); } catch (e) {}
  }
  function strikes() {
    try { return parseInt(sessionStorage.getItem(STRIKE_KEY), 10) || 0; } catch (e) { return 0; }
  }
  function bumpStrikes() {
    var n = strikes() + 1;
    try { sessionStorage.setItem(STRIKE_KEY, String(n)); } catch (e) {}
    return n;
  }

  /* Seconds left on the hold, rounded up. 0 when the shop is answering. */
  function cooling() {
    var left = coolUntil() - now();
    return left > 0 ? Math.ceil(left / 1000) : 0;
  }

  /* How long to wait, in words a person can act on. Below ninety seconds a number
     is useful; above it a number is false precision on a block whose real length
     nobody here knows. */
  function waitText(secs) {
    if (!secs || secs < 1) return 'a moment';
    if (secs < 90) return 'about ' + Math.round(secs) + ' seconds';
    var mins = Math.round(secs / 60);
    return 'about ' + mins + (mins === 1 ? ' minute' : ' minutes');
  }

  /* The body a caller gets when we refuse locally. Shaped like Shopify's own 429
     so code that reads .status or .description finds what it expects. */
  function refusalBody(secs) {
    return JSON.stringify({
      status: 429,
      message: 'Too many attempts',
      description: 'The store is busy. Please try again in ' + waitText(secs) + '.'
    });
  }

  function isWrite(input, init) {
    var url = '';
    var method = (init && init.method) || (input && input.method) || 'GET';
    if (typeof input === 'string') url = input;
    else if (input && typeof input.url === 'string') url = input.url;
    else if (input && typeof input.toString === 'function') url = String(input);
    if (String(method).toUpperCase() !== 'POST') return false;
    return WRITE_RE.test(url.split('#')[0]);
  }

  /* The queued, spaced, timed, cool-down-aware fetch. Returns a real Response. */
  function guardedFetch(input, init) {
    var held = cooling();
    if (held) {
      /* Refused here, with nothing sent. This is the whole point: a shop that is
         already refusing must not be asked again by twenty different controls. */
      return Promise.resolve(new Response(refusalBody(held), {
        status: 429,
        headers: { 'Content-Type': 'application/json', 'Retry-After': String(held) }
      }));
    }

    var run = queue.then(function () {
      var wait = Math.max(0, MIN_GAP_MS - (now() - lastAt));
      return wait ? new Promise(function (r) { setTimeout(r, wait); }) : null;
    }).then(function () {
      /* ⛔ CHECK AGAIN AT THE FRONT OF THE QUEUE, NOT ONLY AT THE BACK OF IT.
         The check above the queue only sees the state at the moment a write is
         ENQUEUED. Five writes queued while the shop was healthy would all still go
         out even after the first came back 429 — the exact behaviour this file
         exists to stop, just moved one layer down. The decision belongs at the
         moment of sending. */
      if (cooling()) return null;
      lastAt = now();
      var ctrl = typeof AbortController !== 'undefined' ? new AbortController() : null;
      var timer = setTimeout(function () { if (ctrl) ctrl.abort(); }, TIMEOUT_MS);
      var opts = init || {};
      if (ctrl) {
        /* copy rather than mutate: the caller may reuse its init object */
        var copy = {};
        for (var k in opts) if (Object.prototype.hasOwnProperty.call(opts, k)) copy[k] = opts[k];
        copy.signal = ctrl.signal;
        opts = copy;
      }
      return rawFetch(input, opts).then(function (res) {
        clearTimeout(timer);
        return res;
      }, function (err) {
        clearTimeout(timer);
        if (err && err.name === 'AbortError') {
          return new Response(refusalBody(5), {
            status: 429,
            headers: {
              'Content-Type': 'application/json',
              'Retry-After': '5',
              'X-NH-Timeout': '1'      /* marks this as our own giving up, not a refusal */
            }
          });
        }
        throw err;
      });
    }).then(function (res) {
      if (res === null) {
        /* Refused at the FRONT of the queue: between being enqueued and reaching
           the wire, the shop told us to stop. Answer locally, send nothing. */
        var held = cooling();
        return new Response(refusalBody(held), {
          status: 429,
          headers: { 'Content-Type': 'application/json', 'Retry-After': String(held || 30) }
        });
      }
      /* ⛔⛔ 12 Sep 2026 — THE HOLD IS WHAT THE SERVER ASKED FOR AND NOT A SECOND MORE.
         An earlier version of this file doubled the hold on every consecutive
         refusal: 60s, 120s, 240s. The reasoning was to stop knocking at a long
         block. The effect, the first time the owner tried it, was a cart that told
         him to wait 51 seconds and then 79 seconds, each attempt worse than the
         last: *"even worse"*. He was right. The thing that actually stops us
         hammering is the serialising and the local refusal inside the window the
         server itself named. Doubling on top of that adds nothing but a longer
         wait for the one person trying to find out whether the shop works. The
         strike count survives, but only to choose the wording. */
      if (res.status === 429 || res.status === 430 || res.status === 503) {
        if (res.headers.get('X-NH-Timeout')) {
          /* ⛔ A TIMEOUT IS NOT A REFUSAL. This synthetic 429 is us giving up on a
             slow answer, not the shop turning us away. Counting it as a strike made
             a merely slow server escalate exactly like a refusing one, which is very
             probably what the owner was looking at. Short pause, no strike. */
          setCool(TIMEOUT_HOLD_MS);
        } else {
          bumpStrikes();
          var hdr = parseInt(res.headers.get('Retry-After'), 10);
          setCool(hdr > 0 ? hdr * 1000 : COOLDOWN_MS);
        }
      } else if (res.ok) {
        clearCool();
      }
      return res;
    });

    /* ⛔ THE TAIL MUST NEVER STAY REJECTED. Chaining the next write onto a rejected
       promise would reject it too, without ever reaching the network, and one
       dropped connection would silently kill every cart write for the rest of the
       page. The queue keeps only the ordering, never the outcome. */
    queue = run.catch(function () {});
    return run;
  }

  /* Explicit API for our own code: same pipeline, but the answer already read.
     Resolves to { ok, status, body, throttled, retryIn }. NEVER rejects on an HTTP
     status: a refusal is an answer, and the caller has to know which kind it was.
     It rejects only when the network itself failed. */
  function write(url, body) {
    return guardedFetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Requested-With': 'XMLHttpRequest'
      },
      body: JSON.stringify(body)
    }).then(function (res) {
      /* A throttled shop can answer with an HTML error page (Cloudflare 1015), so
         the body parse has to be allowed to fail without losing the status. */
      return res.text().then(function (txt) {
        var j = {};
        try { j = JSON.parse(txt); } catch (e) {}
        var throttled = res.status === 429 || res.status === 430 || res.status === 503;
        return {
          ok: res.ok && !j.status,
          status: res.status,
          body: j,
          throttled: throttled,
          retryIn: throttled ? (cooling() || 60) : 0
        };
      });
    });
  }

  /* ⛔ FAIL OPEN, ALWAYS. This function now sits in front of every request the
     site makes. A bug in it would not degrade the cart, it would take the whole
     storefront down. So any error deciding or queueing hands the call straight to
     the real fetch: worst case the site behaves exactly as it did before this
     file existed, which is a bad day, not a dead shop. */
  window.fetch = function (input, init) {
    var guarded = false;
    try { guarded = isWrite(input, init); } catch (e) { guarded = false; }
    if (!guarded) return rawFetch(input, init);
    try { return guardedFetch(input, init); } catch (e) { return rawFetch(input, init); }
  };

  window.NHCart = {
    write: write,
    cooling: cooling,
    clearCool: clearCool,
    strikes: strikes,
    waitText: waitText,
    isWrite: isWrite
  };
})();
